import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  getIntakeByToken, 
  startIntake, 
  submitProspectAssessment,
  uploadProspectDocument,
  completeIntake,
  getExternalAssessmentQuestions
} from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import { Alert, AlertDescription } from '../components/ui/alert';
import { toast } from 'sonner';
import { 
  Building2, 
  User, 
  Mail, 
  Phone, 
  MapPin,
  ClipboardCheck,
  FileText,
  Upload,
  CheckCircle2,
  ChevronRight,
  ChevronLeft,
  AlertTriangle,
  Shield,
  Loader2,
  Calendar,
  Users,
  Lock,
  Info
} from 'lucide-react';
import { cn } from '../lib/utils';

// US States list
const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
  'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
  'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
  'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
  'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
  'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
  'Wisconsin', 'Wyoming'
];

// Simplified document categories - 3 types with multiple file support
const DOCUMENT_CATEGORIES = [
  { value: 'insurance_policies', label: 'Insurance Policies', description: 'Upload your current and expiring insurance policies' },
  { value: 'loss_runs', label: 'Loss Runs', description: 'Upload your loss run reports and claims history' },
  { value: 'certificate_of_insurance', label: 'Certificate of Insurance', description: 'Upload your certificates of insurance (COI)' }
];

const STEPS = [
  { id: 1, label: 'Organization', icon: Building2 },
  { id: 2, label: 'Assessment', icon: ClipboardCheck },
  { id: 3, label: 'Documents', icon: FileText },
  { id: 4, label: 'Complete', icon: CheckCircle2 }
];

export default function ProspectIntake() {
  const { token } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [linkData, setLinkData] = useState(null);
  const [error, setError] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [organizationId, setOrganizationId] = useState(null);
  
  // Form data
  const [orgData, setOrgData] = useState({
    organization_name: '',
    org_type: 'sports',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    gl_policy_renewal_date: '',
    // Youth Sports fields
    sport_type: '',
    participant_count: '',
    team_count: '',
    age_range: '',
    season_type: '',
    // Fitness fields
    facility_type: '',
    staff_count: '',
    classes_offered: '',
    operating_hours: '',
    square_footage: '',
    // Professional Sports fields
    league_affiliation: '',
    venue_name: '',
    venue_capacity: '',
    annual_event_count: '',
    avg_attendance: ''
  });
  
  // Assessment data
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [assessmentResults, setAssessmentResults] = useState(null);
  
  // Document uploads - simplified to 3 categories with multiple files each
  const [uploadedDocs, setUploadedDocs] = useState({
    insurance_policies: [],
    loss_runs: [],
    certificate_of_insurance: []
  });
  const [uploadingDoc, setUploadingDoc] = useState(null);

  // Load intake link data
  useEffect(() => {
    const loadLinkData = async () => {
      try {
        const res = await getIntakeByToken(token);
        setLinkData(res.data);
        
        // Pre-fill form with link data
        setOrgData(prev => ({
          ...prev,
          organization_name: res.data.organization_name || '',
          org_type: res.data.org_type || 'sports',
          contact_name: res.data.contact_name || '',
          contact_email: res.data.contact_email || '',
          sport_type: res.data.sport_type || ''
        }));
        
        // If intake is already complete, show completion screen with results
        if (res.data.intake_completed && res.data.organization_id) {
          setOrganizationId(res.data.organization_id);
          if (res.data.assessment_results) {
            setAssessmentResults(res.data.assessment_results);
          }
          if (res.data.documents) {
            setUploadedDocs({
              insurance_policies: res.data.documents.insurance_policies || [],
              loss_runs: res.data.documents.loss_runs || [],
              certificate_of_insurance: res.data.documents.certificate_of_insurance || res.data.documents.certificates || []
            });
          }
          setCurrentStep(4); // Go directly to completion screen
        }
        // If assessment is completed but documents not yet uploaded, go to step 3
        else if (res.data.assessment_completed && res.data.organization_id) {
          setOrganizationId(res.data.organization_id);
          if (res.data.assessment_results) {
            setAssessmentResults(res.data.assessment_results);
          }
          setCurrentStep(3); // Go to documents step
        }
        // If intake already started but not complete, resume from step 2
        else if (res.data.intake_started && res.data.organization_id) {
          setOrganizationId(res.data.organization_id);
          setCurrentStep(2);
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Failed to load intake:', err);
        if (err.response?.status === 404) {
          setError('This intake link is invalid or has expired.');
        } else if (err.response?.status === 410) {
          setError(err.response.data.detail || 'This intake link has expired.');
        } else {
          setError('Failed to load intake information.');
        }
        setLoading(false);
      }
    };
    
    loadLinkData();
  }, [token]);

  // Load assessment questions when reaching step 2
  useEffect(() => {
    const loadQuestions = async () => {
      if (currentStep === 2 && questions.length === 0) {
        try {
          const res = await getExternalAssessmentQuestions(orgData.org_type);
          setQuestions(res.data);
          // Initialize answers
          setAnswers(res.data.map(q => ({ question_id: q.id, answer: null })));
        } catch (err) {
          toast.error('Failed to load assessment questions');
        }
      }
    };
    loadQuestions();
  }, [currentStep, orgData.org_type, questions.length]);

  // Handle step 1: Start intake
  const handleStartIntake = async () => {
    if (!orgData.organization_name || !orgData.contact_name || !orgData.contact_email || !orgData.state) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSubmitting(true);
    try {
      const res = await startIntake(token, orgData);
      setOrganizationId(res.data.organization_id);
      toast.success('Organization profile saved!');
      setCurrentStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start intake');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle assessment answer
  const handleAnswer = (questionId, answer) => {
    setAnswers(prev => prev.map(a => 
      a.question_id === questionId ? { ...a, answer } : a
    ));
  };

  // Submit assessment
  const handleSubmitAssessment = async () => {
    const unanswered = answers.filter(a => a.answer === null);
    if (unanswered.length > 0) {
      toast.error(`Please answer all questions (${unanswered.length} remaining)`);
      return;
    }
    
    setSubmitting(true);
    try {
      const res = await submitProspectAssessment(token, { answers });
      setAssessmentResults(res.data);
      toast.success('Assessment completed!');
      setCurrentStep(3);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit assessment');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle document upload - supports multiple files per category
  const handleDocUpload = async (files, category) => {
    setUploadingDoc(category);
    try {
      for (const file of files) {
        const res = await uploadProspectDocument(token, file, category);
        const docInfo = {
          id: res.data.document_id,
          filename: res.data.filename,
          category: res.data.category,
          category_display: res.data.category_display
        };
        
        setUploadedDocs(prev => ({
          ...prev,
          [category]: [...prev[category], docInfo]
        }));
      }
      toast.success(`${files.length} file(s) uploaded successfully!`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploadingDoc(null);
    }
  };

  // Remove uploaded document
  const handleRemoveDoc = (category, docId) => {
    setUploadedDocs(prev => ({
      ...prev,
      [category]: prev[category].filter(d => d.id !== docId)
    }));
  };

  // Check if all required documents are uploaded
  const hasRequiredDocs = () => {
    return uploadedDocs.insurance_policies.length > 0 || 
           uploadedDocs.loss_runs.length > 0 || 
           uploadedDocs.certificate_of_insurance.length > 0;
  };

  // Get total document count
  const getTotalDocCount = () => {
    return uploadedDocs.insurance_policies.length + 
           uploadedDocs.loss_runs.length + 
           uploadedDocs.certificate_of_insurance.length;
  };

  // Complete intake
  const handleCompleteIntake = async () => {
    setSubmitting(true);
    try {
      await completeIntake(token);
      toast.success('Intake completed successfully!');
      setCurrentStep(4);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to complete intake');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading intake form...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <AlertTriangle className="w-16 h-16 text-destructive mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Link Error</h2>
            <p className="text-muted-foreground mb-4">{error}</p>
            <Button variant="outline" onClick={() => navigate('/')}>
              Go to Homepage
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-4xl mx-auto px-3 sm:px-4 py-3 sm:py-4">
          <div className="flex flex-col items-center gap-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_safetyguard-4/artifacts/97rz3phl_players-health-lockup-color.png"
              alt="Players Health" 
              className="h-8 sm:h-10"
            />
            <h1 className="text-base sm:text-lg font-semibold text-gray-700 text-center">Risk Assessment Intake Form</h1>
          </div>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-2 sm:px-4 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            {STEPS.map((step, index) => (
              <React.Fragment key={step.id}>
                <div className="flex flex-col items-center">
                  <div 
                    className={cn(
                      "w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center mb-1",
                      currentStep >= step.id 
                        ? "bg-primary text-primary-foreground" 
                        : "bg-muted text-muted-foreground"
                    )}
                  >
                    {currentStep > step.id ? (
                      <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5" />
                    ) : (
                      <step.icon className="w-4 h-4 sm:w-5 sm:h-5" />
                    )}
                  </div>
                  <span className={cn(
                    "text-[10px] sm:text-xs text-center",
                    currentStep >= step.id ? "text-primary font-medium" : "text-muted-foreground"
                  )}>
                    {step.label}
                  </span>
                </div>
                {index < STEPS.length - 1 && (
                  <div className={cn(
                    "flex-1 h-0.5 mx-1 sm:mx-2",
                    currentStep > step.id ? "bg-primary" : "bg-muted"
                  )} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-3 sm:px-4 py-4 sm:py-8">
        {/* Step 1: Organization Info */}
        {currentStep === 1 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                Organization Information
              </CardTitle>
              <CardDescription>
                Tell us about your organization
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label>Organization Name *</Label>
                  <Input
                    value={orgData.organization_name}
                    onChange={(e) => setOrgData({ ...orgData, organization_name: e.target.value })}
                    placeholder="e.g., Metro Youth Soccer League"
                    data-testid="org-name-input"
                  />
                </div>
                
                <div>
                  <Label>Organization Type *</Label>
                  <Select 
                    value={orgData.org_type} 
                    onValueChange={(value) => setOrgData({ ...orgData, org_type: value, sport_type: '', participant_count: '', team_count: '' })}
                  >
                    <SelectTrigger data-testid="org-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sports">Youth Sports</SelectItem>
                      <SelectItem value="fitness">Fitness</SelectItem>
                      <SelectItem value="professional_sports">Professional Sports</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Youth Sports Fields */}
                {orgData.org_type === 'sports' && (
                  <>
                    <div>
                      <Label>Sport Type</Label>
                      <Select 
                        value={orgData.sport_type} 
                        onValueChange={(value) => setOrgData({ ...orgData, sport_type: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select sport" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Soccer">Soccer</SelectItem>
                          <SelectItem value="Basketball">Basketball</SelectItem>
                          <SelectItem value="Baseball">Baseball</SelectItem>
                          <SelectItem value="Football">Football</SelectItem>
                          <SelectItem value="Hockey">Hockey</SelectItem>
                          <SelectItem value="Swimming">Swimming</SelectItem>
                          <SelectItem value="Gymnastics">Gymnastics</SelectItem>
                          <SelectItem value="Volleyball">Volleyball</SelectItem>
                          <SelectItem value="Track & Field">Track & Field</SelectItem>
                          <SelectItem value="Multi-Sport">Multi-Sport</SelectItem>
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label>Number of Athletes</Label>
                      <Input
                        type="number"
                        value={orgData.participant_count}
                        onChange={(e) => setOrgData({ ...orgData, participant_count: e.target.value })}
                        placeholder="e.g., 500"
                      />
                    </div>
                    
                    <div>
                      <Label>Number of Teams</Label>
                      <Input
                        type="number"
                        value={orgData.team_count}
                        onChange={(e) => setOrgData({ ...orgData, team_count: e.target.value })}
                        placeholder="e.g., 25"
                      />
                    </div>
                    
                    <div>
                      <Label>Age Range of Participants</Label>
                      <Input
                        value={orgData.age_range}
                        onChange={(e) => setOrgData({ ...orgData, age_range: e.target.value })}
                        placeholder="e.g., 5-18"
                      />
                    </div>
                    
                    <div>
                      <Label>Season Type</Label>
                      <Select 
                        value={orgData.season_type} 
                        onValueChange={(value) => setOrgData({ ...orgData, season_type: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select season type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Year-round">Year-round</SelectItem>
                          <SelectItem value="Fall">Fall Season</SelectItem>
                          <SelectItem value="Winter">Winter Season</SelectItem>
                          <SelectItem value="Spring">Spring Season</SelectItem>
                          <SelectItem value="Summer">Summer Season</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </>
                )}
                
                {/* Fitness Organization Fields */}
                {orgData.org_type === 'fitness' && (
                  <>
                    <div>
                      <Label>Facility Type</Label>
                      <Select 
                        value={orgData.facility_type} 
                        onValueChange={(value) => setOrgData({ ...orgData, facility_type: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select facility type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Gym">Full-Service Gym</SelectItem>
                          <SelectItem value="Studio">Fitness Studio</SelectItem>
                          <SelectItem value="CrossFit">CrossFit Box</SelectItem>
                          <SelectItem value="Yoga">Yoga Studio</SelectItem>
                          <SelectItem value="Pilates">Pilates Studio</SelectItem>
                          <SelectItem value="Personal Training">Personal Training Studio</SelectItem>
                          <SelectItem value="Boutique">Boutique Fitness</SelectItem>
                          <SelectItem value="Multi-purpose">Multi-purpose Facility</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label>Number of Members</Label>
                      <Input
                        type="number"
                        value={orgData.participant_count}
                        onChange={(e) => setOrgData({ ...orgData, participant_count: e.target.value })}
                        placeholder="e.g., 500"
                      />
                    </div>
                    
                    <div>
                      <Label>Number of Staff/Trainers</Label>
                      <Input
                        type="number"
                        value={orgData.staff_count}
                        onChange={(e) => setOrgData({ ...orgData, staff_count: e.target.value })}
                        placeholder="e.g., 15"
                      />
                    </div>
                    
                    <div>
                      <Label>Classes Offered</Label>
                      <Input
                        value={orgData.classes_offered}
                        onChange={(e) => setOrgData({ ...orgData, classes_offered: e.target.value })}
                        placeholder="e.g., Group Fitness, Personal Training, Aquatics"
                      />
                    </div>
                    
                    <div>
                      <Label>Operating Hours</Label>
                      <Input
                        value={orgData.operating_hours}
                        onChange={(e) => setOrgData({ ...orgData, operating_hours: e.target.value })}
                        placeholder="e.g., 5am - 10pm, 24/7"
                      />
                    </div>
                    
                    <div>
                      <Label>Square Footage</Label>
                      <Input
                        type="number"
                        value={orgData.square_footage}
                        onChange={(e) => setOrgData({ ...orgData, square_footage: e.target.value })}
                        placeholder="e.g., 15,000"
                      />
                    </div>
                  </>
                )}
                
                {/* Professional Sports Fields */}
                {orgData.org_type === 'professional_sports' && (
                  <>
                    <div>
                      <Label>Sport Type</Label>
                      <Select 
                        value={orgData.sport_type} 
                        onValueChange={(value) => setOrgData({ ...orgData, sport_type: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select sport" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Football (NFL)">Football (NFL)</SelectItem>
                          <SelectItem value="Basketball (NBA)">Basketball (NBA)</SelectItem>
                          <SelectItem value="Baseball (MLB)">Baseball (MLB)</SelectItem>
                          <SelectItem value="Hockey (NHL)">Hockey (NHL)</SelectItem>
                          <SelectItem value="Soccer (MLS)">Soccer (MLS)</SelectItem>
                          <SelectItem value="Minor League">Minor League</SelectItem>
                          <SelectItem value="Independent League">Independent League</SelectItem>
                          <SelectItem value="College Athletics">College Athletics</SelectItem>
                          <SelectItem value="Other Professional">Other Professional</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label>League/Association Affiliation</Label>
                      <Input
                        value={orgData.league_affiliation}
                        onChange={(e) => setOrgData({ ...orgData, league_affiliation: e.target.value })}
                        placeholder="e.g., NFL, NBA, NCAA Division I"
                      />
                    </div>
                    
                    <div>
                      <Label>Venue Name</Label>
                      <Input
                        value={orgData.venue_name}
                        onChange={(e) => setOrgData({ ...orgData, venue_name: e.target.value })}
                        placeholder="e.g., MetLife Stadium"
                      />
                    </div>
                    
                    <div>
                      <Label>Venue Capacity</Label>
                      <Input
                        type="number"
                        value={orgData.venue_capacity}
                        onChange={(e) => setOrgData({ ...orgData, venue_capacity: e.target.value })}
                        placeholder="e.g., 82,500"
                      />
                    </div>
                    
                    <div>
                      <Label>Number of Athletes/Players</Label>
                      <Input
                        type="number"
                        value={orgData.participant_count}
                        onChange={(e) => setOrgData({ ...orgData, participant_count: e.target.value })}
                        placeholder="e.g., 53"
                      />
                    </div>
                    
                    <div>
                      <Label>Number of Staff</Label>
                      <Input
                        type="number"
                        value={orgData.staff_count}
                        onChange={(e) => setOrgData({ ...orgData, staff_count: e.target.value })}
                        placeholder="e.g., 200"
                      />
                    </div>
                    
                    <div>
                      <Label>Annual Event Count</Label>
                      <Input
                        type="number"
                        value={orgData.annual_event_count}
                        onChange={(e) => setOrgData({ ...orgData, annual_event_count: e.target.value })}
                        placeholder="e.g., 81 home games"
                      />
                    </div>
                    
                    <div>
                      <Label>Average Attendance per Event</Label>
                      <Input
                        type="number"
                        value={orgData.avg_attendance}
                        onChange={(e) => setOrgData({ ...orgData, avg_attendance: e.target.value })}
                        placeholder="e.g., 40,000"
                      />
                    </div>
                  </>
                )}
              </div>

              <hr className="my-4" />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Contact Name *</Label>
                  <Input
                    value={orgData.contact_name}
                    onChange={(e) => setOrgData({ ...orgData, contact_name: e.target.value })}
                    placeholder="John Smith"
                    data-testid="contact-name-input"
                  />
                </div>
                
                <div>
                  <Label>Contact Email *</Label>
                  <Input
                    type="email"
                    value={orgData.contact_email}
                    onChange={(e) => setOrgData({ ...orgData, contact_email: e.target.value })}
                    placeholder="john@organization.com"
                    data-testid="contact-email-input"
                  />
                </div>
                
                <div>
                  <Label>Contact Phone</Label>
                  <Input
                    value={orgData.contact_phone}
                    onChange={(e) => setOrgData({ ...orgData, contact_phone: e.target.value })}
                    placeholder="(555) 123-4567"
                  />
                </div>
                
                <div>
                  <Label>GL Policy Renewal Date</Label>
                  <Input
                    type="date"
                    value={orgData.gl_policy_renewal_date}
                    onChange={(e) => setOrgData({ ...orgData, gl_policy_renewal_date: e.target.value })}
                  />
                </div>
              </div>

              <hr className="my-4" />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label>Address</Label>
                  <Input
                    value={orgData.address}
                    onChange={(e) => setOrgData({ ...orgData, address: e.target.value })}
                    placeholder="123 Main Street"
                  />
                </div>
                
                <div>
                  <Label>City</Label>
                  <Input
                    value={orgData.city}
                    onChange={(e) => setOrgData({ ...orgData, city: e.target.value })}
                    placeholder="City name"
                  />
                </div>
                
                <div>
                  <Label>State *</Label>
                  <Select 
                    value={orgData.state} 
                    onValueChange={(value) => setOrgData({ ...orgData, state: value })}
                  >
                    <SelectTrigger data-testid="state-select">
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent>
                      {US_STATES.map(state => (
                        <SelectItem key={state} value={state}>{state}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>ZIP Code</Label>
                  <Input
                    value={orgData.zip_code}
                    onChange={(e) => setOrgData({ ...orgData, zip_code: e.target.value })}
                    placeholder="12345"
                  />
                </div>
              </div>

              <div className="flex justify-end pt-4">
                <Button onClick={handleStartIntake} disabled={submitting} data-testid="continue-btn">
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      Continue
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Assessment */}
        {currentStep === 2 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ClipboardCheck className="w-5 h-5" />
                Risk Assessment
              </CardTitle>
              <CardDescription>
                Answer the following questions to assess your organization's risk profile
              </CardDescription>
            </CardHeader>
            <CardContent>
              {questions.length === 0 ? (
                <div className="flex items-center justify-center p-8">
                  <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <>
                  <Progress 
                    value={(answers.filter(a => a.answer !== null).length / questions.length) * 100} 
                    className="mb-6" 
                  />
                  <p className="text-sm text-muted-foreground mb-4">
                    Question {currentQuestion + 1} of {questions.length}
                  </p>
                  
                  <div className="space-y-6">
                    <div className="p-4 bg-muted rounded-lg" key={`question-${currentQuestion}`}>
                      <p className="font-medium mb-4">
                        {questions[currentQuestion]?.question}
                      </p>
                      {questions[currentQuestion]?.description && (
                        <p className="text-sm text-muted-foreground mb-4">
                          {questions[currentQuestion].description}
                        </p>
                      )}

                      {/* Prominent "Where applicable" callout for aquatic / conditional questions */}
                      {(questions[currentQuestion]?.allow_na || /where applicable/i.test(questions[currentQuestion]?.question || '')) && (
                        <div className="mb-4 p-3 rounded-md bg-amber-500/10 border border-amber-500/40 flex items-start gap-2">
                          <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                          <p className="text-sm text-amber-700 dark:text-amber-300">
                            <strong>Not applicable to your organization?</strong> Select <strong>N/A</strong> and this question will be excluded from your score.
                          </p>
                        </div>
                      )}
                      
                      {/* Handle multiple choice questions */}
                      {questions[currentQuestion]?.type === 'multiple_choice' && questions[currentQuestion]?.options ? (
                        <RadioGroup
                          key={`mc-${questions[currentQuestion]?.id}-${currentQuestion}`}
                          value={answers[currentQuestion]?.answer !== null && answers[currentQuestion]?.answer !== undefined 
                            ? answers[currentQuestion].answer.toString() 
                            : ""}
                          onValueChange={(value) => handleAnswer(
                            questions[currentQuestion].id, 
                            value
                          )}
                        >
                          <div className="space-y-2">
                            {questions[currentQuestion].options.map((option, idx) => (
                              <div key={option.value} className="flex items-center space-x-2">
                                <RadioGroupItem value={option.value} id={`option-${currentQuestion}-${idx}`} />
                                <Label htmlFor={`option-${currentQuestion}-${idx}`}>{option.label}</Label>
                              </div>
                            ))}
                          </div>
                        </RadioGroup>
                      ) : (
                        /* Handle yes/no questions */
                        <RadioGroup
                          key={`yn-${questions[currentQuestion]?.id}-${currentQuestion}`}
                          value={answers[currentQuestion]?.answer !== null && answers[currentQuestion]?.answer !== undefined 
                            ? answers[currentQuestion].answer.toString() 
                            : ""}
                          onValueChange={(value) => handleAnswer(
                            questions[currentQuestion].id, 
                            value === "na" ? "na" : parseInt(value)
                          )}
                        >
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="1" id={`yes-${currentQuestion}`} />
                            <Label htmlFor={`yes-${currentQuestion}`}>Yes</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="0" id={`no-${currentQuestion}`} />
                            <Label htmlFor={`no-${currentQuestion}`}>No</Label>
                          </div>
                          {(questions[currentQuestion]?.allow_na || /where applicable/i.test(questions[currentQuestion]?.question || '')) && (
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="na" id={`na-${currentQuestion}`} />
                              <Label htmlFor={`na-${currentQuestion}`}>N/A (Not applicable)</Label>
                            </div>
                          )}
                        </RadioGroup>
                      )}
                    </div>

                    <div className="flex justify-between">
                      <Button
                        variant="outline"
                        onClick={() => setCurrentQuestion(prev => Math.max(0, prev - 1))}
                        disabled={currentQuestion === 0}
                      >
                        <ChevronLeft className="w-4 h-4 mr-2" />
                        Previous
                      </Button>
                      
                      {currentQuestion < questions.length - 1 ? (
                        <Button
                          onClick={() => setCurrentQuestion(prev => prev + 1)}
                          disabled={answers[currentQuestion]?.answer === null}
                        >
                          Next
                          <ChevronRight className="w-4 h-4 ml-2" />
                        </Button>
                      ) : (
                        <Button
                          onClick={handleSubmitAssessment}
                          disabled={submitting || answers.some(a => a.answer === null)}
                        >
                          {submitting ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Submitting...
                            </>
                          ) : (
                            <>
                              Submit Assessment
                              <CheckCircle2 className="w-4 h-4 ml-2" />
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* Step 3: Policy Upload */}
        {currentStep === 3 && (
          <div className="space-y-6">
            {/* Assessment Results with Score AND Recommendations */}
            {assessmentResults && (
              <Card className={cn(
                "border-2",
                assessmentResults.risk_level === 'low' && "bg-green-50 border-green-200",
                assessmentResults.risk_level === 'medium' && "bg-yellow-50 border-yellow-200",
                assessmentResults.risk_level === 'elevated' && "bg-orange-50 border-orange-200",
                assessmentResults.risk_level === 'high' && "bg-red-50 border-red-200"
              )}>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className={cn(
                        "font-semibold text-lg",
                        assessmentResults.risk_level === 'low' && "text-green-700",
                        assessmentResults.risk_level === 'medium' && "text-yellow-700",
                        assessmentResults.risk_level === 'elevated' && "text-orange-700",
                        assessmentResults.risk_level === 'high' && "text-red-700"
                      )}>
                        Assessment Complete!
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Your risk readiness score based on your responses
                      </p>
                    </div>
                    <div className="text-center">
                      <div className={cn(
                        "text-4xl font-bold",
                        assessmentResults.risk_level === 'low' && "text-green-600",
                        assessmentResults.risk_level === 'medium' && "text-yellow-600",
                        assessmentResults.risk_level === 'elevated' && "text-orange-600",
                        assessmentResults.risk_level === 'high' && "text-red-600"
                      )}>
                        {assessmentResults.score?.toFixed(0)}%
                      </div>
                      <Badge className={cn(
                        assessmentResults.risk_level === 'low' && "bg-green-100 text-green-800",
                        assessmentResults.risk_level === 'medium' && "bg-yellow-100 text-yellow-800",
                        assessmentResults.risk_level === 'elevated' && "bg-orange-100 text-orange-800",
                        assessmentResults.risk_level === 'high' && "bg-red-100 text-red-800"
                      )}>
                        {assessmentResults.risk_level === 'medium' ? 'Moderate' : assessmentResults.risk_level?.charAt(0).toUpperCase() + assessmentResults.risk_level?.slice(1)} Risk
                      </Badge>
                    </div>
                  </div>
                  
                  {/* Show Recommendations Immediately */}
                  {assessmentResults.recommendations?.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <h4 className="font-semibold text-left mb-3 flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5 text-amber-500" />
                        Recommended Actions Based on Your Assessment
                      </h4>
                      <ul className="space-y-2 text-left">
                        {assessmentResults.recommendations.map((rec, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm">
                            <div className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-2 flex-shrink-0" aria-hidden="true" />
                            <span>{rec}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Document Upload
                </CardTitle>
                <CardDescription>
                  Upload your insurance documents, loss runs, and certificates of insurance
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <Alert>
                  <AlertDescription>
                    Upload at least one document to continue. You can upload multiple files for each category. 
                    Supported formats: PDF, DOC, DOCX, XLS, XLSX
                  </AlertDescription>
                </Alert>

                {/* Three upload categories */}
                {DOCUMENT_CATEGORIES.map(cat => (
                  <div key={cat.value} className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <Label className="text-base font-semibold">{cat.label}</Label>
                        <p className="text-sm text-muted-foreground">{cat.description}</p>
                      </div>
                      {uploadedDocs[cat.value]?.length > 0 && (
                        <Badge variant="secondary">
                          {uploadedDocs[cat.value].length} file(s)
                        </Badge>
                      )}
                    </div>
                    
                    <Input
                      type="file"
                      accept=".pdf,.doc,.docx,.xls,.xlsx"
                      multiple
                      disabled={uploadingDoc === cat.value}
                      onChange={(e) => {
                        if (e.target.files?.length > 0) {
                          handleDocUpload(Array.from(e.target.files), cat.value);
                        }
                      }}
                      className="mb-3"
                    />
                    
                    {/* Show uploaded files */}
                    {uploadedDocs[cat.value]?.length > 0 && (
                      <div className="space-y-2 mt-3">
                        {uploadedDocs[cat.value].map(doc => (
                          <div key={doc.id} className="flex items-center justify-between text-sm bg-muted p-2 rounded">
                            <div className="flex items-center gap-2">
                              <CheckCircle2 className="w-4 h-4 text-green-500" />
                              <span>{doc.filename}</span>
                            </div>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              onClick={() => handleRemoveDoc(cat.value, doc.id)}
                              className="h-6 px-2 text-muted-foreground hover:text-destructive"
                            >
                              ×
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {uploadingDoc === cat.value && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mt-2">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Uploading...
                      </div>
                    )}
                  </div>
                ))}

                <div className="flex justify-between pt-4">
                  <Button variant="outline" onClick={() => setCurrentStep(2)}>
                    <ChevronLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                  <Button 
                    onClick={handleCompleteIntake}
                    disabled={!hasRequiredDocs() || submitting}
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Completing...
                      </>
                    ) : (
                      <>
                        Complete Intake
                        <CheckCircle2 className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Step 4: Complete */}
        {currentStep === 4 && (
          <Card className="text-center">
            <CardContent className="p-6 sm:p-12">
              <CheckCircle2 className="w-16 h-16 sm:w-20 sm:h-20 text-green-500 mx-auto mb-6" />
              <h2 className="text-xl sm:text-2xl font-bold mb-4">Intake Complete!</h2>
              
              <div className="max-w-lg mx-auto mb-6 space-y-4 text-left">
                <p className="text-muted-foreground text-sm sm:text-base">
                  Thank you for completing your risk assessment intake. By sharing this information, 
                  you are helping take an important step toward creating a safer environment 
                  for the athletes, families, and communities you serve.
                </p>
              </div>
              
              {/* Risk Score Display */}
              {assessmentResults && (
                <div className="max-w-2xl mx-auto mb-6">
                  <div className={cn(
                    "rounded-lg p-6 border-2",
                    assessmentResults.risk_level === 'low' && "bg-green-50 border-green-200",
                    assessmentResults.risk_level === 'medium' && "bg-yellow-50 border-yellow-200",
                    assessmentResults.risk_level === 'elevated' && "bg-orange-50 border-orange-200",
                    assessmentResults.risk_level === 'high' && "bg-red-50 border-red-200"
                  )}>
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-4">
                      <div>
                        <h3 className="font-semibold text-lg">Your Risk Readiness Score</h3>
                        <p className="text-sm text-muted-foreground">
                          Based on your assessment responses
                        </p>
                      </div>
                      <div className="text-center">
                        <div className={cn(
                          "text-4xl font-bold",
                          assessmentResults.risk_level === 'low' && "text-green-600",
                          assessmentResults.risk_level === 'medium' && "text-yellow-600",
                          assessmentResults.risk_level === 'elevated' && "text-orange-600",
                          assessmentResults.risk_level === 'high' && "text-red-600"
                        )}>
                          {assessmentResults.score?.toFixed(0)}%
                        </div>
                        <Badge className={cn(
                          assessmentResults.risk_level === 'low' && "bg-green-100 text-green-800",
                          assessmentResults.risk_level === 'medium' && "bg-yellow-100 text-yellow-800",
                          assessmentResults.risk_level === 'elevated' && "bg-orange-100 text-orange-800",
                          assessmentResults.risk_level === 'high' && "bg-red-100 text-red-800"
                        )}>
                          {assessmentResults.risk_level === 'medium' ? 'Moderate' : assessmentResults.risk_level?.charAt(0).toUpperCase() + assessmentResults.risk_level?.slice(1)} Risk
                        </Badge>
                      </div>
                    </div>
                    
                    {/* Recommendations Section */}
                    {assessmentResults.recommendations?.length > 0 && (
                      <div className="mt-6 pt-6 border-t border-gray-200">
                        <h4 className="font-semibold text-left mb-3 flex items-center gap-2">
                          <AlertTriangle className="w-5 h-5 text-amber-500" />
                          Recommended Actions
                        </h4>
                        <ul className="space-y-2 text-left">
                          {assessmentResults.recommendations.map((rec, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm">
                              <div className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-2 flex-shrink-0" aria-hidden="true" />
                              <span>{rec}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Documents Summary */}
              <div className="bg-muted rounded-lg p-6 max-w-md mx-auto mb-6">
                <h3 className="font-semibold mb-4">Documents Submitted</h3>
                <div className="space-y-2 text-sm text-left">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Organization</span>
                    <span className="font-medium">{orgData.organization_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Insurance Policies</span>
                    <span className="font-medium">{uploadedDocs.insurance_policies.length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Loss Runs</span>
                    <span className="font-medium">{uploadedDocs.loss_runs.length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Certificates of Insurance</span>
                    <span className="font-medium">{uploadedDocs.certificate_of_insurance.length}</span>
                  </div>
                </div>
                
                {/* Documents locked message */}
                <div className="mt-4 pt-4 border-t border-gray-300">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Lock className="w-4 h-4" />
                    <span>Your documents will be available for download after account activation.</span>
                  </div>
                </div>
              </div>
              
              {/* Next Steps */}
              <div className="max-w-lg mx-auto text-left bg-blue-50 rounded-lg p-4 mb-6">
                <h4 className="font-semibold mb-2 flex items-center gap-2">
                  <Info className="w-5 h-5 text-blue-600" />
                  What Happens Next?
                </h4>
                <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
                  <li>A Players Health Team Member will review your submission</li>
                  <li>You'll receive an email to complete your account setup</li>
                  <li>Once activated, you'll have full access to:
                    <ul className="ml-6 mt-1 space-y-1 list-disc">
                      <li>Your risk management dashboard</li>
                      <li>Your uploaded documents</li>
                      <li>Tools and resources to help address your recommendations</li>
                    </ul>
                  </li>
                </ol>
              </div>
              
              <p className="text-sm text-muted-foreground">
                You may close this window. We appreciate the work you do to make sports a positive and safe experience.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
